import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import useMediaQuery from '../../../../utils/breakpoints/useMediaQuery';
import { useBookStatistics } from '../../../../hooks/useBookStatistics';
import { BookStatisticsWrapper, StatCard, BookItem, SectionHeader, BookStatisticsTitleWrapper } from './styled';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../../constants/colors';

const BookStatisticsChart = () => {
  const { isMobile } = useMediaQuery();
  const { data, isLoading, isError, error } = useBookStatistics();
  const { t } = useTranslation();

  const getSectionColor = (sectionName: string): string => {
    switch (sectionName) {
      case 'Art-therapy':
        return COLORS.artTherapy;
      case 'Kids':
        return COLORS.kids;
      case 'Youth':
        return COLORS.youth;
      case 'Teens':
        return COLORS.teens;
      case 'Event-lab':
        return COLORS.eventLab;
      case 'Show':
        return COLORS.show;
      default:
        return COLORS.magenta;
    }
  };

  const getTotalPurchases = () => {
    if (!data?.statistics) return 0;
    return data.statistics.reduce((sum, section) => sum + section.total_purchases, 0);
  };

  const getMaxPurchaseCount = () => {
    if (!data?.statistics) return 0;
    let max = 0;
    data.statistics.forEach(section => {
      section.books.forEach(book => {
        if (book.purchase_count > max) {
          max = book.purchase_count;
        }
      });
    });
    return max;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error?.message || 'An error occurred while fetching statistics'}
      </Alert>
    );
  }

  if (!data?.statistics || data.statistics.length === 0) {
    return (
      <Alert severity="info" sx={{ margin: 2 }}>
        No statistics available
      </Alert>
    );
  }

  const maxCount = getMaxPurchaseCount();

  return (
    <BookStatisticsWrapper isMobile={isMobile}>
      <BookStatisticsTitleWrapper>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontFamily: 'Montserrat, sans-serif !important' }}
          >
            Статистика покупок книг
          </Typography>

          <Box mb={3}>
            <Chip
              label={`Всего покупок: ${getTotalPurchases()}`}
              color="primary"
              size="medium"
              sx={{ fontFamily: 'Montserrat, sans-serif !important' }}
            />
          </Box>
      </BookStatisticsTitleWrapper>
          <Grid container spacing={3}>
            {data.statistics.map((section, index) => {
              const sectionColor = getSectionColor(section.section);

              return (
              <Grid item xs={12} md={6} key={index}>
                <StatCard elevation={2}>
                  <SectionHeader>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontFamily: 'Montserrat, sans-serif !important',
                        color: sectionColor
                      }}
                    >
                      {section.section}
                    </Typography>
                    <Chip
                      label={`${section.total_purchases} покупок`}
                      size="small"
                      sx={{
                        fontFamily: 'Montserrat, sans-serif !important',
                        backgroundColor: sectionColor,
                        color: COLORS.white,
                        fontWeight: 600
                      }}
                    />
                  </SectionHeader>

                  <Box mt={2}>
                    {section.books.map((book, bookIndex) => {
                      const percentage = maxCount > 0 ? (book.purchase_count / maxCount) * 100 : 0;

                      return (
                        <BookItem key={bookIndex}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ fontFamily: 'Montserrat, sans-serif !important' }}
                            >
                              {t(`course.${book.name}.title`)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: 'Montserrat, sans-serif !important' }}
                            >
                              {book.purchase_count}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: '8px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              borderRadius: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${percentage}%`,
                                height: '100%',
                                backgroundColor: sectionColor,
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </Box>
                        </BookItem>
                      );
                    })}
                  </Box>
                </StatCard>
              </Grid>
            );
            })}
          </Grid>
    </BookStatisticsWrapper>
  );
};

export default BookStatisticsChart;

